// src/components/ProfitDashboard.js
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp
} from 'firebase/firestore';

export default function ProfitDashboard() {
  const [startDate, setStartDate] = useState('');
  const [endDate,   setEndDate]   = useState('');
  const [sales,     setSales]     = useState('');
  const [wages,     setWages]     = useState('');
  const [utilities, setUtilities] = useState('');
  const [other,     setOther]     = useState('');
  const [profit,    setProfit]    = useState(null);

  const [recipes,   setRecipes]   = useState([]);
  const [priceMap,  setPriceMap]  = useState({});
  const [gelatoBreakdown, setGelatoBreakdown] = useState([]);
  const [totals, setTotals] = useState(null);

  const fmtDate = (ts) => {
    try {
      const d = ts?.toDate ? ts.toDate() : null;
      return d ? d.toISOString().split('T')[0] : '';
    } catch {
      return '';
    }
  };

  useEffect(() => {
    (async () => {
      const stockSnap = await getDocs(collection(db, 'stock_store'));
      const pMap = {};
      stockSnap.forEach(d => (pMap[d.id.toUpperCase()] = d.data().pricePerKg || 0));
      setPriceMap(pMap);

      const recSnap = await getDocs(collection(db, 'recipes'));
      setRecipes(recSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, []);

  const calcProfit = async () => {
    if (!startDate || !endDate) {
      alert('Please select a date range');
      return;
    }

    const start = new Date(startDate);
    const end   = new Date(endDate);
    end.setDate(end.getDate() + 1);

    const qLogs = query(
      collection(db, 'production_logs'),
      where('timestamp', '>=', Timestamp.fromDate(start)),
      where('timestamp', '<',  Timestamp.fromDate(end))
    );
    const logsSnap = await getDocs(qLogs);

    let totalCost = 0;
    const breakdown = [];
    let totalPans = 0;
    const byFlavour = {};

    logsSnap.forEach(log => {
      const { flavour, quantity, timestamp } = log.data();
      const recipe = recipes.find(r => r.name === flavour);
      const pans = Number(quantity) || 0;

      totalPans += pans;
      byFlavour[flavour] = (byFlavour[flavour] || 0) + pans;

      let flavourCost = 0;
      if (recipe) {
        const ingredients = (recipe.ingredients || []).filter(
          x => !(x.product || '').toLowerCase().includes('water')
        );
        const costPerPan = ingredients.reduce((sum, i) => {
          const code = (i.code || '').toUpperCase();
          const price = priceMap[code] || 0;
          return sum + (i.amount_g / 1000) * price;
        }, 0);
        flavourCost = costPerPan * pans;
        totalCost += flavourCost;
      }

      breakdown.push({
        flavour,
        pans,
        cost: flavourCost.toFixed(2),
        date: fmtDate(timestamp)
      });
    });

    const s = parseFloat(sales) || 0;
    const w = parseFloat(wages) || 0;
    const u = parseFloat(utilities) || 0;
    const o = parseFloat(other) || 0;
    const net = s - (totalCost + w + u + o);

    setProfit({
      sales: s.toFixed(2),
      gelatoCost: totalCost.toFixed(2),
      labour: w.toFixed(2),
      utilities: u.toFixed(2),
      other: o.toFixed(2),
      net: net.toFixed(2)
    });

    setGelatoBreakdown(breakdown);

    const sortedByFlavour = Object.entries(byFlavour).sort(
      (a, b) => b[1] - a[1]
    );
    setTotals({
      totalPans,
      byFlavour: sortedByFlavour
    });
  };

  return (
    <div className="profit-dashboard">
      <h2>📈 Profit Dashboard</h2>

      <div className="profit-card">
        <div className="profit-grid">
          <div>
            <label>Start Date</label>
            <input
              type="date"
              className="input"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label>End Date</label>
            <input
              type="date"
              className="input"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label>Total Sales (€ net)</label>
            <input
              type="number"
              className="input"
              value={sales}
              onChange={e => setSales(e.target.value)}
            />
          </div>
          <div>
            <label>Labour (€)</label>
            <input
              type="number"
              className="input"
              value={wages}
              onChange={e => setWages(e.target.value)}
            />
          </div>
          <div>
            <label>Utilities (€)</label>
            <input
              type="number"
              className="input"
              value={utilities}
              onChange={e => setUtilities(e.target.value)}
            />
          </div>
          <div>
            <label>Other Expenses (€)</label>
            <input
              type="number"
              className="input"
              value={other}
              onChange={e => setOther(e.target.value)}
            />
          </div>
        </div>

        <button
          className="btn btn-primary"
          style={{ width: '100%', marginTop: 20 }}
          onClick={calcProfit}
        >
          💰 Calculate Profit
        </button>
      </div>

      {profit && (
        <div
          style={{
            marginTop: 30,
            padding: 20,
            background: '#f9f9f9',
            borderRadius: 12,
            boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
          }}
        >
          <h3 style={{ textAlign: 'center', marginBottom: 20 }}>
            Results: {startDate} → {endDate}
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td>Sales</td><td>€{profit.sales}</td></tr>
              <tr><td>Gelato COGS (based on production logs)</td><td>€{profit.gelatoCost}</td></tr>
              <tr><td>Labour</td><td>€{profit.labour}</td></tr>
              <tr><td>Utilities</td><td>€{profit.utilities}</td></tr>
              <tr><td>Other</td><td>€{profit.other}</td></tr>
            </tbody>
          </table>
          <hr style={{ margin: '20px 0' }} />
          <h2 style={{ textAlign: 'center', color: '#0a7d0a' }}>
            Net Profit: €{profit.net}
          </h2>

          {totals && (
            <div style={{ marginTop: 20 }}>
              <h4>🍨 Gelato Produced</h4>
              <p style={{ margin: '6px 0' }}>
                <strong>Total pans:</strong> {totals.totalPans}
              </p>
              {totals.byFlavour.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer' }}>
                    Pans by flavour (click to expand)
                  </summary>
                  <ul style={{ marginLeft: 18, marginTop: 8 }}>
                    {totals.byFlavour.map(([name, pans]) => (
                      <li key={name}>
                        {name}: {pans} pan(s)
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {gelatoBreakdown.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <h4>Gelato Cost Breakdown</h4>
              <ul style={{ marginLeft: 20 }}>
                {gelatoBreakdown.map((g, i) => (
                  <li key={i}>
                    [{g.date}] {g.flavour}: {g.pans} pan(s) → €{g.cost}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginTop: 20, fontSize: '0.9rem', lineHeight: 1.5 }}>
            <strong>How Gelato Cost is Calculated:</strong>
            <ul>
              <li>Reads every production log between the selected dates.</li>
              <li>
                Finds the recipe for each flavour and its ingredient prices from{' '}
                <code>stock_store</code>.
              </li>
              <li>Totals <em>cost-per-pan × number of pans produced</em>.</li>
              <li>All batches count as cost whether sold or discarded.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
